/**
 * TanStack Query hook + error mapping for public company self-registration
 * (`POST /api/v1/registration/company`).
 */
import { useMutation } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

import { registerCompany } from './registrationApi';
import type { CompanyRegistrationRequest, CompanyRegistrationResult } from './registrationApi';

/** Provision a new company (tenant + owner user) via the BFF; the owner then verifies their email. */
export function useRegisterCompany(): UseMutationResult<
  CompanyRegistrationResult,
  NormalizedApiError,
  CompanyRegistrationRequest
> {
  return useMutation<CompanyRegistrationResult, NormalizedApiError, CompanyRegistrationRequest>({
    mutationFn: registerCompany,
  });
}

/**
 * Map a registration failure to a user-facing message, mirroring the documented
 * `POST /api/v1/registration/company` responses (`openapi.yml`):
 *  - `503` / `IDENTITY_UNAVAILABLE` — identity provisioning temporarily unavailable;
 *  - `400` — request validation failed.
 * Falls back to the normalised server message for anything else.
 */
export function registrationErrorMessage(error: NormalizedApiError): string {
  if (error.status === 503 || error.code === 'IDENTITY_UNAVAILABLE') {
    return 'Sign-up is temporarily unavailable. Please try again in a few minutes.';
  }
  if (error.status === 400) {
    return 'Please check the details and try again.';
  }
  return error.message || 'Could not create your company. Please try again.';
}
